import { useEffect, useRef, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';

export function AccountMenu({
  name,
  isAdmin,
}: {
  readonly name: string;
  readonly isAdmin: boolean;
}): JSX.Element {
  const { logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: PointerEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const close = (): void => setOpen(false);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        className="flex items-center gap-2 rounded-[var(--kitup-radius-sm)] px-2 py-1.5 text-sm font-medium focus-visible:ring-2 focus-visible:ring-[var(--kitup-color-accent)]/50 focus-visible:outline-none"
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="account-menu"
        aria-label={`${t.settings.accountMenu}: ${name}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{name}</span>
        <span aria-hidden="true" className="text-xs text-[var(--kitup-color-muted)]">
          ▾
        </span>
      </button>

      {open ? (
        <nav
          id="account-menu"
          aria-label={t.settings.accountMenu}
          className="kitup-card absolute right-0 z-50 mt-2 grid min-w-52 gap-1 p-2 shadow-lg"
        >
          <AccountLink to="/app" onClick={close}>
            {t.books.title}
          </AccountLink>
          <AccountLink to="/app/characters" onClick={close}>
            {t.characters.title}
          </AccountLink>
          <AccountLink to="/app/things" onClick={close}>
            {t.things.title}
          </AccountLink>
          <AccountLink to="/app/locations" onClick={close}>
            {t.locations.title}
          </AccountLink>
          {isAdmin ? (
            <>
              <AccountLink to="/admin/book-builder" onClick={close}>
                {t.admin.bookBuilder.title}
              </AccountLink>
              <AccountLink to="/admin/usage" onClick={close}>
                {t.admin.usage.title}
              </AccountLink>
              <AccountLink to="/admin/accounts" onClick={close}>
                {t.admin.accounts.title}
              </AccountLink>
              <AccountLink to="/admin/promotions" onClick={close}>
                {t.admin.promotions.title}
              </AccountLink>
              <AccountLink to="/admin/generations" onClick={close}>
                {t.admin.title}
              </AccountLink>
            </>
          ) : null}
          <div className="my-1 border-t border-[var(--kitup-color-muted)]/20" />
          <AccountLink to="/app/settings" onClick={close}>
            {t.settings.title}
          </AccountLink>
          <button
            type="button"
            className="rounded-[var(--kitup-radius-sm)] px-3 py-2 text-left text-sm hover:bg-[var(--kitup-color-paper)] focus-visible:ring-2 focus-visible:ring-[var(--kitup-color-accent)]/50 focus-visible:outline-none"
            onClick={() => {
              close();
              void logout().then(() => navigate('/login', { replace: true }));
            }}
          >
            {t.auth.signOut}
          </button>
        </nav>
      ) : null}
    </div>
  );
}

function AccountLink({
  to,
  onClick,
  children,
}: {
  readonly to: string;
  readonly onClick: () => void;
  readonly children: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="rounded-[var(--kitup-radius-sm)] px-3 py-2 text-sm hover:bg-[var(--kitup-color-paper)] focus-visible:ring-2 focus-visible:ring-[var(--kitup-color-accent)]/50 focus-visible:outline-none"
    >
      {children}
    </Link>
  );
}
